import { AlertCircle, Info, KeyRound, Plus, RefreshCw } from "lucide-react";
import { useState } from "react";

import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { InternalApiKeyFormDrawer } from "../components/InternalApiKeyFormDrawer";
import { InternalApiKeyIpsDialog } from "../components/InternalApiKeyIpsDialog";
import { InternalApiKeyRow } from "../components/InternalApiKeyRow";
import { InternalApiKeyTokenBanner } from "../components/InternalApiKeyTokenBanner";
import { InternalApiKeyUsageDrawer } from "../components/InternalApiKeyUsageDrawer";
import { useInternalApiKeyAccess } from "../hooks/useInternalApiKeyAccess";
import { useInternalApiKeys } from "../hooks/useInternalApiKeys";
import { InternalApiKeysService } from "../services/internalApiKeys.service";
import type { InternalApiKey, InternalApiKeyAllowedIp } from "../types/internalApiKeys.types";
import { toPayload, toUpdatePayload, type InternalApiKeyFormValues } from "../utils/internalApiKeyForm";

type FormTarget = { apiKey: InternalApiKey | null } | null;
type ShownToken = { token: string; kind: "created" | "rotated"; keyName: string } | null;

export default function InternalApiKeysPage() {
  const { t } = useTranslations("internalApiKeys");
  const { access } = useInternalApiKeyAccess();
  const keys = useInternalApiKeys();
  const catalog = useOrganizationsCatalog();
  const twoFactor = useTwoFactorAction<unknown>();

  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  const [token, setToken] = useState<ShownToken>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rotating, setRotating] = useState<InternalApiKey | null>(null);
  const [retireNow, setRetireNow] = useState(false);
  const [revoking, setRevoking] = useState<InternalApiKey | null>(null);
  const [usageOf, setUsageOf] = useState<InternalApiKey | null>(null);
  const [ipsOf, setIpsOf] = useState<InternalApiKey | null>(null);

  if (!keys.hasAccess) return <NoAccessView />;

  const toastError = (error: unknown) => colocaToast.error(HttpApiService.getErrorMessage(error));

  const submitForm = async (values: InternalApiKeyFormValues): Promise<boolean> => {
    const editing = formTarget?.apiKey ?? null;
    const patch = editing ? toUpdatePayload(values, editing) : null;
    if (editing && patch && Object.keys(patch).length === 0) return true;
    const result =
      editing && patch
        ? await twoFactor.start((code) => InternalApiKeysService.update(editing.id, patch, code))
        : await twoFactor.start((code) => InternalApiKeysService.create(toPayload(values), code));
    if (result === null) return false;
    if (editing) {
      colocaToast.success(t("toast_updated"));
    } else {
      const created = result as { key: InternalApiKey; token: string };
      setToken({ token: created.token, kind: "created", keyName: created.key.name });
      colocaToast.success(t("toast_created"));
    }
    keys.reload();
    return true;
  };

  const submitIps = async (apiKey: InternalApiKey, ips: InternalApiKeyAllowedIp[]): Promise<boolean> => {
    const updated = await twoFactor.start((code) =>
      InternalApiKeysService.update(apiKey.id, { allowed_ips: ips }, code),
    );
    if (updated === null) return false;
    keys.replace(updated as InternalApiKey);
    colocaToast.success(t("toast_ips_updated"));
    return true;
  };

  const toggle = async (apiKey: InternalApiKey, next: boolean) => {
    setBusyId(apiKey.id);
    try {
      if (next) {
        const updated = await twoFactor.start((code) => InternalApiKeysService.enable(apiKey.id, code));
        if (updated === null) return;
        keys.replace(updated as InternalApiKey);
        colocaToast.success(t("toast_enabled"));
      } else {
        keys.replace(await InternalApiKeysService.disable(apiKey.id));
        colocaToast.success(t("toast_disabled"));
      }
    } catch (error: unknown) {
      toastError(error);
      // The refusal may be the row being stale (an IP removed, an expiry passed): show what is true now.
      keys.reload();
    } finally {
      setBusyId(null);
    }
  };

  const rotate = async (apiKey: InternalApiKey, retirePreviousNow: boolean) => {
    setRotating(null);
    try {
      const result = await twoFactor.start((code) =>
        InternalApiKeysService.rotate(apiKey.id, code, retirePreviousNow),
      );
      if (result === null) return;
      const rotated = result as { key: InternalApiKey; token: string };
      keys.replace(rotated.key);
      setToken({ token: rotated.token, kind: "rotated", keyName: rotated.key.name });
      colocaToast.success(t("toast_rotated"));
    } catch (error: unknown) {
      toastError(error);
    }
  };

  const revoke = async (apiKey: InternalApiKey) => {
    setRevoking(null);
    try {
      keys.replace(await InternalApiKeysService.revoke(apiKey.id));
      colocaToast.success(t("toast_revoked"));
    } catch (error: unknown) {
      toastError(error);
    }
  };

  const graceHours = access?.rotation_grace_hours ?? 0;

  return (
    <div className="mx-auto w-full max-w-[1100px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <ColocaIconButton
              icon={RefreshCw}
              variant="ghost"
              size="sm"
              aria-label={t("action_refresh")}
              className={cn(keys.isLoading && "[&_svg]:animate-spin")}
              onClick={keys.reload}
            />
            <ColocaButton variant="primary" icon={Plus} onClick={() => setFormTarget({ apiKey: null })}>
              {t("action_new")}
            </ColocaButton>
          </div>
        }
      />

      {access && !access.surface_enabled && (
        <ColocaAlert variant="secondary" icon={Info} text={t("surface_off")} />
      )}

      {token && (
        <InternalApiKeyTokenBanner
          token={token.token}
          kind={token.kind}
          keyName={token.keyName}
          onDismiss={() => setToken(null)}
        />
      )}

      {keys.error ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={keys.error}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={keys.reload}>
              {t("action_retry")}
            </ColocaButton>
          }
        />
      ) : keys.isLoading && keys.keys.length === 0 ? (
        <ColocaSkeleton className="h-40 w-full rounded-2xl" />
      ) : keys.keys.length === 0 ? (
        <ColocaEmpty icon={KeyRound} title={t("empty_title")} description={t("empty_description")} />
      ) : (
        <ColocaSurface>
          <ul className="divide-y divide-border">
            {keys.keys.map((apiKey) => (
              <InternalApiKeyRow
                key={apiKey.id}
                apiKey={apiKey}
                isBusy={busyId === apiKey.id}
                onToggle={toggle}
                onEdit={(target) => setFormTarget({ apiKey: target })}
                onEditIps={setIpsOf}
                onRotate={(target) => {
                  setRetireNow(false);
                  setRotating(target);
                }}
                onUsage={setUsageOf}
                onRevoke={setRevoking}
              />
            ))}
          </ul>
        </ColocaSurface>
      )}

      {formTarget && access && (
        <InternalApiKeyFormDrawer
          key={formTarget.apiKey?.id ?? "new"}
          open
          apiKey={formTarget.apiKey}
          permissionCatalog={access.permissions}
          ipOptionalPermissions={access.ip_optional_permissions ?? []}
          maxTtlDays={access.max_ttl_days}
          catalog={catalog}
          onSubmit={submitForm}
          onClose={() => setFormTarget(null)}
        />
      )}

      {ipsOf && (
        <InternalApiKeyIpsDialog
          key={ipsOf.id}
          apiKey={ipsOf}
          onSubmit={(ips) => submitIps(ipsOf, ips)}
          onClose={() => setIpsOf(null)}
        />
      )}

      <InternalApiKeyUsageDrawer apiKey={usageOf} onClose={() => setUsageOf(null)} />

      <ColocaAlertDialog
        open={rotating !== null}
        onOpenChange={(open) => !open && setRotating(null)}
        title={t("rotate_title", { name: rotating?.name ?? "" })}
        description={
          graceHours > 0 && !retireNow
            ? t("rotate_description_grace", { hours: graceHours })
            : t("rotate_description_now")
        }
        confirmLabel={t("rotate_confirm")}
        confirmVariant={retireNow ? "destructive" : undefined}
        cancelLabel={t("cancel")}
        onConfirm={() => rotating && void rotate(rotating, retireNow)}
      >
        {graceHours > 0 && (
          <ColocaCheckbox
            checked={retireNow}
            onCheckedChange={setRetireNow}
            label={t("rotate_retire_now_label")}
            description={t("rotate_retire_now_hint")}
          />
        )}
      </ColocaAlertDialog>

      <ColocaAlertDialog
        open={revoking !== null}
        onOpenChange={(open) => !open && setRevoking(null)}
        title={t("revoke_title", { name: revoking?.name ?? "" })}
        description={t("revoke_description")}
        confirmLabel={t("revoke_confirm")}
        confirmVariant="destructive"
        cancelLabel={t("cancel")}
        onConfirm={() => revoking && void revoke(revoking)}
      />

      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("two_factor_required")}
        challengeDescription={t("two_factor_challenge")}
      />
    </div>
  );
}
